import * as React from 'react';

export interface Toast {
  id: string;
  title?: string | undefined;
  description?: string | undefined;
  variant?: 'default' | 'destructive' | undefined;
}

// Global toast state management
let toastListeners: ((toasts: Toast[]) => void)[] = [];
let toasts: Toast[] = [];
let toastIdCounter = 0;

function notifyListeners() {
  toastListeners.forEach((listener) => listener([...toasts]));
}

export function addToast(toast: Omit<Toast, 'id'>): string {
  const id = `toast-${++toastIdCounter}`;
  toasts = [...toasts, { ...toast, id }];
  notifyListeners();

  // Auto-dismiss after 5 seconds
  setTimeout(() => {
    dismissToast(id);
  }, 5000);

  return id;
}

export function dismissToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  notifyListeners();
}

export function useToastState() {
  const [state, setState] = React.useState<Toast[]>(toasts);

  React.useEffect(() => {
    const listener = (newToasts: Toast[]) => setState(newToasts);
    toastListeners.push(listener);
    return () => {
      toastListeners = toastListeners.filter((l) => l !== listener);
    };
  }, []);

  return state;
}

export function useToast() {
  const toast = React.useCallback(
    ({
      title,
      description,
      variant = 'default',
    }: {
      title?: string;
      description?: string;
      variant?: 'default' | 'destructive';
    }) => {
      return addToast({ title, description, variant });
    },
    []
  );

  return { toast, dismiss: dismissToast };
}
